"use client";

import { useEffect, useState } from "react";
import { DrawerFieldShell } from "@aera/data-table";
import { AeraButton } from "@aera/ui";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import {
  canEditReceivableCustomerName,
  pickCustomerName,
  postEditReceivableCustomerName,
} from "@/lib/receivableCorrections";

const INPUT_CLASS =
  "w-full border-0 bg-transparent p-0 text-sm font-semibold leading-6 text-aera-charcoal shadow-none placeholder:text-aera-lightslate placeholder:font-normal focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-aera-lightslate";

function fieldValueForInput(value: string, fallback = ""): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "—") return fallback;
  return trimmed;
}

export function ReceivableCustomerNameEditor({
  item,
  label,
  value,
  ownerUserId,
  workspaceId,
  disabled = false,
  onApplied,
}: {
  item: ReceivableListItem;
  label: string;
  value: string;
  ownerUserId: string | null;
  workspaceId: string | null;
  disabled?: boolean;
  onApplied?: () => void | Promise<void>;
}) {
  const receivableId =
    item.receivableId?.trim() ||
    item.fields.find((field) => field.key === "receivable_id")?.value?.trim() ||
    null;
  const originalName = pickCustomerName(item);
  const editable = canEditReceivableCustomerName(item);

  const [editName, setEditName] = useState(() =>
    fieldValueForInput(value, originalName),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEditName(fieldValueForInput(value, originalName));
    setError(null);
  }, [item.mergeKey, originalName, value]);

  const canAct = Boolean(
    editable &&
      ownerUserId?.trim() &&
      workspaceId?.trim() &&
      receivableId &&
      !disabled,
  );

  const trimmed = editName.trim();
  const unchanged = trimmed === originalName.trim();
  const invalid = !trimmed;

  if (!editable) {
    return null;
  }

  const handleSave = async () => {
    if (!canAct || !receivableId || !ownerUserId || !workspaceId) return;
    if (unchanged || invalid) return;

    setBusy(true);
    setError(null);
    try {
      await postEditReceivableCustomerName({
        receivableId,
        ownerUserId,
        workspaceId,
        customerName: trimmed,
        receivableItem: item,
      });
      await onApplied?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "แก้ชื่อลูกค้าไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 px-4 py-3">
      <label
        htmlFor="receivable-field-customer_ref"
        className="block text-xs font-semibold leading-5 text-aera-lightslate"
      >
        {label}
      </label>
      <DrawerFieldShell editable={!disabled && canAct}>
        <input
          id="receivable-field-customer_ref"
          type="text"
          value={editName}
          disabled={!canAct || busy}
          placeholder="ยังไม่ระบุ"
          onChange={(event) => setEditName(event.target.value)}
          className={INPUT_CLASS}
        />
      </DrawerFieldShell>

      {!unchanged && invalid ? (
        <p className="text-xs font-medium text-aera-red">กรุณาระบุชื่อลูกค้า</p>
      ) : null}

      {!unchanged && canAct && !invalid ? (
        <AeraButton
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => void handleSave()}
        >
          {busy ? "กำลังบันทึก…" : "บันทึกชื่อลูกค้า"}
        </AeraButton>
      ) : null}

      {error ? (
        <p className="text-xs font-medium text-aera-red">{error}</p>
      ) : null}
    </div>
  );
}
